<template>
    <Teleport to="body">
        <Transition name="confirm">
            <div v-if="state.open" class="confirm-backdrop" @mousedown.self="answer(false)">
                <div ref="panel" class="confirm-panel" :class="{danger:o.danger}" role="alertdialog" aria-modal="true"
                     :aria-labelledby="'confirm-title'" tabindex="-1">

                    <div class="confirm-head">
                        <span v-if="o.icon" class="material-symbols-outlined confirm-icon" aria-hidden="true">{{ o.icon }}</span>
                        <h2 id="confirm-title" class="confirm-title">{{ o.title || 'Are you sure?' }}</h2>
                    </div>

                    <p v-if="o.message" class="confirm-message">{{ o.message }}</p>

                    <ul v-if="o.details && o.details.length" class="confirm-details">
                        <li v-for="(line,i) in o.details" :key="i">{{ line }}</li>
                    </ul>

                    <p v-if="o.warning" class="confirm-warning">
                        <span class="material-symbols-outlined" aria-hidden="true">warning</span>
                        <span>{{ o.warning }}</span>
                    </p>

                    <form v-if="o.input" class="confirm-input" @submit.prevent="answer(true)">
                        <label for="confirm-input">{{ o.input.label }}</label>
                        <div class="confirm-input-row">
                            <input id="confirm-input" ref="field" v-model="state.value"
                                   :type="o.input.secret && !shown ? 'password' : 'text'"
                                   :placeholder="o.input.placeholder || ''" autocomplete="off" autocapitalize="off" spellcheck="false">
                            <button v-if="o.input.secret" type="button" class="confirm-eye"
                                    :aria-label="shown ? 'Hide' : 'Show'" :title="shown ? 'Hide' : 'Show'"
                                    @click.stop="shown=!shown">
                                <span class="material-symbols-outlined" aria-hidden="true">{{ shown ? 'visibility_off' : 'visibility' }}</span>
                            </button>
                        </div>
                        <p v-if="o.input.hint" class="confirm-input-hint">{{ o.input.hint }}</p>
                    </form>

                    <div class="confirm-buttons">
                        <button type="button" class="confirm-cancel" @click.stop="answer(false)">{{ o.cancel_label || 'Cancel' }}</button>
                        <button ref="ok" type="button" class="confirm-ok" @click.stop="answer(true)">{{ o.confirm_label || 'OK' }}</button>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>


<script setup>
    import {computed,ref,watch,nextTick,onMounted,onBeforeUnmount} from 'vue'
    import {confirm_state as state,answer} from '@/confirm'

    const o=computed(()=>state.options||{})
    const ok=ref(null)
    const field=ref(null)
    const shown=ref(false)
    let opener=null

    watch(()=>state.open,async open=>{
        if(open){
            opener=document.activeElement
            shown.value=false
            await nextTick()
            // With a text box, start typing straight away; otherwise sit on the confirm button.
            if(field.value){ field.value.focus() }else if(ok.value){ ok.value.focus() }
        }else if(opener && document.body.contains(opener)){
            opener.focus()
            opener=null
        }
    })

    function on_keydown(event){
        if(!state.open){ return }
        if(event.key=='Escape'){
            // Answer here, before any edit window underneath sees the Escape.
            event.stopImmediatePropagation()
            answer(false)
        }
    }

    // Capture phase, so the dialog answers Escape before the windows below it.
    onMounted(()=>document.addEventListener('keydown',on_keydown,true))
    onBeforeUnmount(()=>document.removeEventListener('keydown',on_keydown,true))
</script>


<style scoped>

    .confirm-backdrop{
        position:fixed;
        inset:0;
        z-index:3000;       /* above edit windows (1000+) and the popups */
        display:flex;
        align-items:center;
        justify-content:center;
        padding:16px;
        background:rgba(15,20,30,0.5);
    }

    .confirm-panel{
        display:flex;
        flex-direction:column;
        gap:12px;
        width:min(440px,100%);
        max-height:100%;
        overflow:auto;
        padding:20px 22px 18px;
        font-size:15px;
        color:#2d2f35;
        background:#ffffff;
        border-radius:14px;
        box-shadow:0 24px 60px rgba(0,0,0,0.3);
        outline:none;
    }
    .confirm-panel,
    .confirm-panel *:not(.material-symbols-outlined){
        font-family:system-ui,-apple-system,'Segoe UI',Roboto,'Helvetica Neue',Arial,sans-serif;
    }

    .confirm-head{
        display:flex;
        align-items:center;
        gap:10px;
    }
    .confirm-icon{
        display:flex;
        align-items:center;
        justify-content:center;
        flex:none;
        width:40px;
        height:40px;
        font-size:22px;
        color:#2a8a6e;
        background:rgba(42,138,110,0.12);
        border-radius:50%;
    }
    .danger .confirm-icon{
        color:#b3261e;
        background:rgba(179,38,30,0.10);
    }
    .confirm-title{
        margin:0;
        font-size:18px;
        font-weight:600;
        line-height:1.3;
    }
    .confirm-message{
        margin:0;
        line-height:1.45;
        white-space:pre-line;
        color:#45484f;
    }

    .confirm-details{
        display:flex;
        flex-direction:column;
        gap:4px;
        max-height:40vh;
        overflow:auto;
        margin:0;
        padding:8px 12px;
        list-style:none;
        font-size:14px;
        background:#f5f3ef;
        border:1px solid #e4dfd7;
        border-radius:8px;
    }

    .confirm-warning{
        display:flex;
        align-items:flex-start;
        gap:8px;
        margin:0;
        padding:8px 10px;
        font-size:14px;
        line-height:1.4;
        color:#7a4f00;
        background:#fff6e0;
        border:1px solid #f1dca6;
        border-radius:8px;
    }
    .confirm-warning .material-symbols-outlined{ font-size:20px; }

    .confirm-input{
        display:flex;
        flex-direction:column;
        gap:6px;
        margin:0;
    }
    .confirm-input label{
        font-size:13px;
        font-weight:600;
        color:#45484f;
    }
    .confirm-input-row{
        display:flex;
        align-items:center;
        background:#ffffff;
        border:1px solid #d3cdc4;
        border-radius:10px;
    }
    .confirm-input-row:focus-within{
        outline:2px solid #06739e;
        outline-offset:-1px;
    }
    .confirm-input-row input{
        flex:1;
        min-width:0;
        height:44px;
        padding:0 12px;
        font-size:16px;     /* 16px stops iOS zooming in on focus */
        color:#2d2f35;
        background:transparent;
        border:none;
        outline:none;
    }
    .confirm-buttons .confirm-eye,
    .confirm-eye{
        display:flex;
        align-items:center;
        justify-content:center;
        width:44px;
        min-width:44px;
        height:44px;
        padding:0;
        color:#71747c;
        background:transparent;
        border:none;
        box-shadow:none;
    }
    .confirm-eye:hover{
        color:#2d2f35;
        background:transparent;
        box-shadow:none;
    }
    .confirm-input-hint{
        margin:0;
        font-size:12.5px;
        color:#71747c;
    }

    .confirm-buttons{
        display:flex;
        flex-wrap:wrap;
        justify-content:flex-end;
        gap:8px;
        margin-top:4px;
    }
    .confirm-buttons button{
        display:inline-flex;
        align-items:center;
        justify-content:center;
        min-width:96px;
        height:44px;
        min-height:44px;
        padding:0 18px;
        font-size:15px;
        font-weight:600;
        word-break:normal;
        border-radius:10px;
        box-shadow:none;
        cursor:pointer;
    }
    .confirm-cancel{
        color:#2d2f35;
        background:#ffffff;
        border:1px solid #d3cdc4;
    }
    .confirm-cancel:hover{
        color:#2d2f35;
        background:#f5f3ef;
        box-shadow:none;
    }
    .confirm-ok{
        color:#ffffff;
        background:#2a8a6e;
        border:1px solid #2a8a6e;
    }
    .confirm-ok:hover{
        color:#ffffff;
        background:#22735b;
        box-shadow:none;
    }
    .danger .confirm-ok{
        background:#b3261e;
        border-color:#b3261e;
    }
    .danger .confirm-ok:hover{ background:#931f18; }
    .confirm-buttons button:focus-visible{
        outline:2px solid #06739e;
        outline-offset:2px;
    }

    @media (max-width:600px){
        .confirm-backdrop{ align-items:flex-end; padding:0; }
        .confirm-panel{
            width:100%;
            border-radius:16px 16px 0 0;
            padding:18px 16px calc(16px + env(safe-area-inset-bottom));
        }
        .confirm-buttons button{ flex:1 1 0; }
    }

    .confirm-enter-active,.confirm-leave-active{ transition:opacity 0.15s ease; }
    .confirm-enter-from,.confirm-leave-to{ opacity:0; }

</style>
